import { useSeo } from "../lib/seo";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight } from "lucide-react";
import { api, optimizeImg } from "../lib/api";
import { BoaCard } from "../components/BoaCard";
import { Newsletter } from "../components/Newsletter";

const HERO_IMG = "https://images.unsplash.com/photo-1571391733814-15ac29ac3544?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const Chapter = ({ num, title, body }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="grid md:grid-cols-[140px_1fr] gap-6 md:gap-12 py-12 border-t border-white/10"
  >
    <span className="font-display text-5xl md:text-6xl text-qbprimary/90">{num}</span>
    <div>
      <h3 className="font-display text-3xl md:text-4xl mb-4">{title}</h3>
      <p className="text-qbfg/60 max-w-2xl leading-relaxed">{body}</p>
    </div>
  </motion.div>
);

export default function Home() {
  useSeo({});
  const [boas, setBoas] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    api.get("/boas").then((r) => setBoas(r.data)).catch(() => {});
  }, []);

  const featured = boas.filter((b) => b.featured && b.status !== "sold").slice(0, 2);
  const grid = featured.length ? featured : boas.filter((b) => b.status !== "sold").slice(0, 2);

  return (
    <div className="grain">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-screen min-h-[100svh] overflow-hidden">
        <motion.div style={{ y: imgY, scale: imgScale }} className="relative h-[46vh] sm:h-[52vh] lg:absolute lg:inset-0 lg:h-full">
          <img src={optimizeImg(HERO_IMG, 1600)} alt="Apex boa" decoding="async" fetchpriority="high" className="w-full h-full object-cover object-[58%_22%] lg:object-[72%_center]" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/30 lg:via-black/45 lg:to-black/60" />
          <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-black/90 via-black/45 to-transparent" />
        </motion.div>

        <div className="relative z-10 lg:absolute lg:inset-0 lg:flex lg:items-end">
          <div className="max-w-[1400px] mx-auto w-full px-6 md:px-10 pt-8 pb-16 lg:pb-20">
          <motion.span
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="text-xs tracking-[0.3em] uppercase text-qbprimary mb-6"
          >
            High-end Boa constrictor genetics
          </motion.span>
          <h1 className="font-display font-light text-[2.75rem] sm:text-7xl md:text-8xl leading-[1.04] tracking-tight max-w-5xl">
            {[
              <>Living color,</>,
              <>bred for</>,
              <>unrivaled <span className="italic watercolor-text">beauty.</span></>,
            ].map((t, i) => (
              <span key={i} className="block overflow-hidden pb-[0.14em]">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }}
            className="text-qbfg/60 max-w-xl mt-8 text-base sm:text-lg"
          >
            Rare, high-contrast morphs bred for color and clarity — each with fully
            documented lineage and a guaranteed live arrival.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <Link to="/shop" data-testid="hero-shop-btn" className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-qbfg text-qbbg hover:bg-qbprimary transition-colors">
              Explore the collection
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link to="/genetics" data-testid="hero-genetics-btn" className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/25 text-qbfg hover:border-qbprimary hover:text-qbprimary transition-colors">
              Decode the genetics
            </Link>
          </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 py-10 overflow-hidden">
        <Marquee speed={40} gradient={false}>
          {["Kahl Albino", "VPI Albino", "Anery Type 1", "Black Eyed Anery", "Labyrinth", "Blood", "Leopard", "Hypo", "Ghost", "NFI", "JPC Albino", "Jungle", "IMG", "Keywest", "Inca"].map((m, i) => (
            <span key={i} className="font-display text-3xl md:text-5xl text-qbfg/15 mx-10 italic leading-[1.5] inline-block">{m}</span>
          ))}
        </Marquee>
      </div>

      {/* MANIFESTO */}
      <section id="lineage" data-testid="manifesto-section" className="px-6 md:px-10 py-24 md:py-32 max-w-[1400px] mx-auto">
        <div className="max-w-3xl mb-8">
          <span className="text-xs tracking-[0.25em] uppercase text-qbsecondary">The standard</span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.08] pb-1">
            We don't sell snakes.<br />We steward bloodlines.
          </h2>
        </div>
        <Chapter num="01" title="Health above hype" body="Established feeders only. Every animal is arenavirus tested, quarantined, weighed and photographed under honest light. Live arrival guaranteed on every shipment." />
        <Chapter num="02" title="Documented lineage" body="Every animal ships with a complete parentage record — sire, dam, litter date and genetic makeup. You always know exactly what you're breeding into." />
        <Chapter num="03" title="Genetics, made legible" body="We explain what each morph actually does — recessive, incomplete dominant, pattern or base — so collectors and hobbyists can plan projects with confidence, not guesswork." />
      </section>

      {/* FEATURED — Boas of the Month */}
      <section data-testid="featured-section" className="px-6 md:px-10 py-24 md:py-32 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <span className="text-xs tracking-[0.25em] uppercase text-qbprimary">Featured Boas</span>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.1] pb-1">Boas of the Month</h2>
          </div>
          <Link to="/shop" data-testid="featured-viewall" className="group inline-flex items-center gap-2 text-qbfg/70 hover:text-qbprimary transition-colors">
            View full collection
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="featured-grid">
          {grid.map((b) => <BoaCard key={b.id} boa={b} />)}
        </div>
      </section>

      <Newsletter />
    </div>
  );
}
